import React, { useEffect, useState } from "react";
import { client } from "../services/api";
import "./AdminDashboard.css";

interface MetricCard {
  label: string;
  value: string;
  subtext: string;
  icon: string;
  trend?: string;
}

interface TrendData {
  date: string;
  quality_score: number;
  yes_rate: number;
  avg_stars: number;
  response_count: number;
}

interface FeedbackItem {
  id: string;
  resolution_status: string;
  helpfulness_rating: number;
  user_notes: string;
  quality_score: number;
  kb_status: string;
  escalation_suggested: boolean;
  created_at: string;
}

interface Alert {
  id: string;
  quality_score: number;
  resolution_status: string;
  helpfulness_rating: number;
  user_notes: string;
  created_at: string;
  severity: string;
}

export const AdminDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [shouldPoll, setShouldPoll] = useState(true);

  const fetchDashboardData = async () => {
    try {
      // Fetch all dashboard data in parallel using the configured axios client
      const [summaryRes, trendsRes, feedbackRes, alertsRes] = await Promise.all([
        client.get("/admin/dashboard/summary").catch(e => {
          if (e.response?.status === 403) throw new Error("Access denied. Only superadmins can view this dashboard.");
          throw e;
        }),
        client.get("/admin/dashboard/trends", { params: { days: 7 } }),
        client.get("/admin/dashboard/feedback-stream", { params: { limit: 10 } }),
        client.get("/admin/dashboard/alerts", { params: { threshold: 0.5 } }),
      ]);

      setMetrics(summaryRes.data);
      setTrends(trendsRes.data);
      setFeedback(feedbackRes.data);
      setAlerts(alertsRes.data);
      setLastUpdated(new Date().toLocaleTimeString());
      setError(null);
      setShouldPoll(true); // Resume polling on success
    } catch (err) {
      const statusCode = (err as any)?.response?.status;

      // Stop polling on 401/403 auth errors
      if (statusCode === 401 || statusCode === 403) {
        setShouldPoll(false);
      }

      if (err instanceof Error && err.message.includes("Access denied")) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and set up polling
  useEffect(() => {
    fetchDashboardData();

    let interval: NodeJS.Timeout | undefined;
    if (shouldPoll) {
      interval = setInterval(fetchDashboardData, 5000); // Poll every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [shouldPoll]);

  if (loading && !metrics) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  const stats: MetricCard[] = [
    {
      label: "Avg Quality Score",
      value: (metrics?.avg_quality_score || 0).toFixed(2),
      subtext: `/1.0 scale`,
      icon: "📊",
      trend: metrics?.trend_direction,
    },
    {
      label: "Yes Resolution Rate",
      value: `${(metrics?.yes_resolution_rate || 0).toFixed(1)}%`,
      subtext: "problems resolved",
      icon: "✅",
    },
    {
      label: "Avg Star Rating",
      value: (metrics?.avg_star_rating || 0).toFixed(1),
      subtext: `out of 5 stars`,
      icon: "⭐",
    },
    {
      label: "Excellent Responses",
      value: `${(metrics?.excellent_percent || 0).toFixed(1)}%`,
      subtext: "score > 0.8",
      icon: "🏆",
    },
  ];

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>📈 Admin Dashboard</h1>
        <div className="header-info">
          <span className="last-updated">Last updated: {lastUpdated}</span>
          <button type="button" className="refresh-btn" onClick={fetchDashboardData}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {/* Metrics Cards */}
      <div className="metrics-grid">
        {stats.map((stat, i) => (
          <div key={i} className="metric-card">
            <div className="metric-icon">{stat.icon}</div>
            <div className="metric-content">
              <div className="metric-label">{stat.label}</div>
              <div className="metric-value">{stat.value}</div>
              <div className="metric-subtext">{stat.subtext}</div>
              {stat.trend && (
                <div className="metric-trend">{stat.trend}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      <div className="section">
        <h2>📉 7-Day Quality Trend</h2>
        <div className="trend-chart">
          {trends.length > 0 ? (
            <div className="trend-data">
              {trends.map((day, i) => (
                <div key={i} className="trend-row">
                  <span className="date">{day.date}</span>
                  <div className="trend-bar">
                    <div
                      className="bar quality"
                      style={{ width: `${(day.quality_score * 100)}%` }}
                      title={`Quality: ${day.quality_score}`}
                    />
                  </div>
                  <span className="value">{day.quality_score.toFixed(2)}</span>
                  <span className="count">{day.response_count} responses</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No trend data available</p>
          )}
        </div>
      </div>

      {/* Live Feedback Stream */}
      <div className="section">
        <h2>💬 Live Feedback Stream (Latest 10)</h2>
        <div className="feedback-stream">
          {feedback.length > 0 ? (
            feedback.map((item) => (
              <div key={item.id} className="feedback-item">
                <div className="feedback-header">
                  <span className="resolution-badge" data-status={item.resolution_status}>
                    {item.resolution_status.toUpperCase()}
                  </span>
                  <span className="stars">{"⭐".repeat(item.helpfulness_rating)}</span>
                  <span className="quality-score">
                    Quality: {(item.quality_score * 100).toFixed(0)}%
                  </span>
                  <span className="timestamp">
                    {new Date(item.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <div className="feedback-comment">{item.user_notes}</div>
                <div className="feedback-meta">
                  <span className="kb-status">
                    KB: {item.kb_status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="no-data">No feedback yet</p>
          )}
        </div>
      </div>

      {/* Quality Alerts */}
      {alerts.length > 0 && (
        <div className="section alerts-section">
          <h2>🚨 Quality Alerts (Low-Quality Responses)</h2>
          <div className="alerts-list">
            {alerts.map((alert) => (
              <div key={alert.id} className="alert-item" data-severity={alert.severity}>
                <div className="alert-header">
                  <span className="severity">{alert.severity}</span>
                  <span className="score">
                    Quality Score: {(alert.quality_score * 100).toFixed(0)}%
                  </span>
                  <span className="time">
                    {new Date(alert.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <div className="alert-details">
                  <strong>Resolution:</strong> {alert.resolution_status} |
                  <strong> Stars:</strong> {"⭐".repeat(alert.helpfulness_rating)} |
                  <strong> Comment:</strong> {alert.user_notes || "(no comment)"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="section">
        <h2>📊 Additional Stats</h2>
        <div className="stats-summary">
          <div className="stat-box">
            <span className="stat-label">Total Feedback Responses</span>
            <span className="stat-value">{metrics?.total_responses || 0}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Approved for Reuse</span>
            <span className="stat-value">{metrics?.approved_percent || 0}%</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">7-Day Trend</span>
            <span className="stat-value">
              {metrics?.trend_direction} {(metrics?.trend_value || 0).toFixed(3)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
